// todo: Review this file. It's too big with too many methods
/* eslint-disable @typescript-eslint/no-use-before-define */
import { UseFormTools } from "@wisr/common";
import { Field, Fieldset, StatusCallout } from "@wisr/react-ui";
import React, { useState } from "react";
import { useSelector } from "react-redux";

import { RemoveButton } from "../../shared/remove-button/remove-button.component";
import { AppStore } from "../../store";
import { Application, Status } from "../application";
import { frequencies, getDebtFields } from "../application.utils";
import { applicationFormDefaults } from "../application-form.constants";
import { FormCard } from "../income/income.component";

const debtOptions = [
  { label: "Credit card / overdraft / store card", value: "Credit card" },
  "Personal loan",
  "Mortgage",
];

export type Debt = {
  type: string;
  company: string;
  balance: string;
  repaymentAmount: string;
  frequency: string;
  isJoint: string[];
  consolidate: string[];
  investment: string[];
  cardLimit: string;
  closeCard: string[];
};
interface Props {
  fieldAdapter: UseFormTools<typeof applicationFormDefaults>["fieldAdapter"];
  formData: Application;
  setValue: UseFormTools<typeof applicationFormDefaults>["setValue"];
  callouts?: Status[];
}

export type FinancialDebtId = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;
// eslint-disable-next-line prettier/prettier
const financialDebtIds: FinancialDebtId[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
// The last item in the array is assumed to be the maximum count
const [maxFinancialDebtCount] = financialDebtIds.slice(-1);

export const ApplicationOwingComponent: React.FunctionComponent<Props> = ({
  fieldAdapter,
  formData,
  setValue,
  callouts,
}) => {
  const hasUnsecuredRateType = useSelector(
    (state: AppStore) => !state.applicationState.rateCardVersionSecured
  );

  const {
    financialDebtType_1,
    financialDebtType_2,
    financialDebtType_3,
    financialDebtType_4,
    financialDebtType_5,
    financialDebtType_6,
    financialDebtType_7,
    financialDebtType_8,
    financialDebtType_9,
    financialDebtType_10,
  } = formData;

  const [financialDebtCount, setFinancialDebtCount] = useState(() => {
    switch (true) {
      case !!financialDebtType_10:
        return 10;
      case !!financialDebtType_9:
        return 9;
      case !!financialDebtType_8:
        return 8;
      case !!financialDebtType_7:
        return 7;
      case !!financialDebtType_6:
        return 6;
      case !!financialDebtType_5:
        return 5;
      case !!financialDebtType_4:
        return 4;
      case !!financialDebtType_3:
        return 3;
      case !!financialDebtType_2:
        return 2;
      case !!financialDebtType_1:
        return 1;
      default:
        return 0;
    }
  });

  function removeDebt(remove: FinancialDebtId) {
    return () => {
      const debts = removeDebtItem(getDebtFields(formData), remove - 1);
      debts.forEach((debt, i) => {
        const {
          debtTypeKey,
          financeCompanyKey,
          balanceKey,
          repaymentAmountKey,
          repaymentFrequencyKey,
          isJointLoanKey,
          consolidateKey,
          investmentKey,
          cardLimitKey,
          closeCardKey,
        } = getFieldKeys((i + 1) as FinancialDebtId);
        setValue(debtTypeKey, debt.type);
        setValue(financeCompanyKey, debt.company);
        setValue(balanceKey, debt.balance);
        setValue(repaymentAmountKey, debt.repaymentAmount);
        setValue(repaymentFrequencyKey, debt.frequency);
        setValue(isJointLoanKey, debt.isJoint);
        setValue(consolidateKey, debt.consolidate);
        setValue(investmentKey, debt.investment);
        setValue(cardLimitKey, debt.cardLimit);
        setValue(closeCardKey, debt.closeCard);
      });

      clearDebtFieldsFromIndex(debts.length);
    };
  }

  function clearDebtFieldsFromIndex(debtIndex: number) {
    for (let i = debtIndex + 1; i <= 10; i++) {
      const {
        debtTypeKey,
        financeCompanyKey,
        balanceKey,
        repaymentAmountKey,
        repaymentFrequencyKey,
        isJointLoanKey,
        consolidateKey,
        investmentKey,
        cardLimitKey,
        closeCardKey,
      } = getFieldKeys(i as FinancialDebtId);

      setValue(debtTypeKey, "");
      setValue(financeCompanyKey, "");
      setValue(balanceKey, "");
      setValue(repaymentAmountKey, "");
      setValue(repaymentFrequencyKey, "");
      setValue(isJointLoanKey, []);
      setValue(consolidateKey, []);
      setValue(investmentKey, []);
      setValue(cardLimitKey, "");
      setTimeout(() => setValue(closeCardKey, []));
    }

    setFinancialDebtCount(debtIndex);
  }

  function clearAllDebtFields() {
    clearDebtFieldsFromIndex(0);
  }

  function addfinancialDebt(e: React.ChangeEvent<HTMLSelectElement>) {
    const field = `financialDebtType_${
      financialDebtCount + 1
    }` as keyof typeof formData;
    setValue(field, e.currentTarget.value);
    setFinancialDebtCount(financialDebtCount + 1);
    e.currentTarget.blur();

    // this is a hack to force validation as there is a timing issue
    const field2 = `financialDebtFinanceCompany_${
      financialDebtCount + 1
    }` as keyof typeof formData;
    setTimeout(() => setValue(field2, ""));
  }

  function clearSelection(e: React.FocusEvent<HTMLSelectElement>) {
    e.currentTarget.value = "";
  }

  function onDebtDeclarationChange(hasDebts: string) {
    if (hasDebts !== "yes") {
      clearAllDebtFields();
    }
  }

  return (
    <>
      <Fieldset heading="What you owe">
        <p>
          Help us process your loan faster by declaring <strong>all</strong>{" "}
          your debts
        </p>
        <>
          {!isDebtConsolidationPurpose(formData) && (
            <Field
              type="radio-button"
              label="Do you have credit cards, overdrafts, mortgages or loans?"
              options={[
                { label: "Yes, I do", value: "yes" },
                { label: "No, I don't", value: "no" },
              ]}
              {...fieldAdapter({
                name: "hasDebts",
                required: "Please declare if you have any debts",
                onChange: onDebtDeclarationChange,
              })}
            />
          )}
          {showDebtConsolidationInfo(formData, callouts) && (
            <StatusCallout
              type="info"
              message="Since you're consolidating debt, please add the credit card(s) or personal loan(s) that you are consolidating"
            />
          )}
          {callouts &&
            callouts.map((callout, i) => (
              <StatusCallout
                key={`${i}-callout`}
                type={callout.kind}
                message={callout.message}
              />
            ))}

          {financialDebtIds.map((id) => {
            const {
              debtTypeKey,
              financeCompanyKey,
              balanceKey,
              repaymentAmountKey,
              repaymentFrequencyKey,
              isJointLoanKey,
              consolidateKey,
              investmentKey,
              cardLimitKey,
              closeCardKey,
            } = getFieldKeys(id);

            return (
              <FormCard
                hide={id > financialDebtCount}
                key={id}
                heading={formData[debtTypeKey]}
                action={<RemoveButton onClick={removeDebt(id)} />}
              >
                <Field
                  layout="single"
                  label="Finance company name"
                  type="text"
                  {...fieldAdapter({
                    name: financeCompanyKey,
                    required: "Finance company name is required",
                    condition: id <= financialDebtCount,
                  })}
                ></Field>
                <Field
                  type="currency"
                  label="Credit card limit"
                  {...fieldAdapter({
                    name: cardLimitKey,
                    required: "Credit card limit is required",
                    min: [0],
                    condition:
                      id <= financialDebtCount &&
                      formData[debtTypeKey] === "Credit card",
                  })}
                ></Field>
                <Field
                  type="currency"
                  label={
                    formData[debtTypeKey] === "Credit card"
                      ? "Credit card balance"
                      : "Loan Balance"
                  }
                  {...fieldAdapter({
                    name: balanceKey,
                    required: `${
                      formData[debtTypeKey] === "Credit card"
                        ? "Credit card"
                        : "Loan"
                    } Balance is required`,
                    min: [0],
                    condition:
                      id <= financialDebtCount &&
                      showBalance(
                        formData[debtTypeKey],
                        formData[consolidateKey]
                      ),
                  })}
                ></Field>
                <Field
                  type="currency"
                  label="Repayment amount"
                  {...fieldAdapter({
                    name: repaymentAmountKey,
                    required: "Repayment amount is required",
                    min: [0],
                    condition:
                      id <= financialDebtCount &&
                      isLoanOrMortgage(formData[debtTypeKey]),
                  })}
                ></Field>
                <Field
                  type="select"
                  label="Repayment frequency"
                  options={frequencies}
                  placeholder="Select..."
                  {...fieldAdapter({
                    name: repaymentFrequencyKey,
                    required: "Repayment frequency is required",
                    min: [0],
                    condition:
                      id <= financialDebtCount &&
                      isLoanOrMortgage(formData[debtTypeKey]),
                  })}
                ></Field>

                <Field
                  type="checkbox"
                  label=""
                  options={["Joint loan"]}
                  {...fieldAdapter({
                    name: isJointLoanKey,
                    condition:
                      id <= financialDebtCount &&
                      isLoanOrMortgage(formData[debtTypeKey]),
                  })}
                />
                <Field
                  type="checkbox"
                  label=""
                  options={[
                    `Consolidate this ${
                      formData[debtTypeKey] === "Credit card" ? "card" : "loan"
                    }`,
                  ]}
                  {...fieldAdapter({
                    name: consolidateKey,
                    required: checkCallout(callouts),
                    condition:
                      id <= financialDebtCount &&
                      isLoanOrCreditCard(formData[debtTypeKey]) &&
                      hasUnsecuredRateType,
                  })}
                />
                <Field
                  type="checkbox"
                  label=""
                  options={["Investment property"]}
                  {...fieldAdapter({
                    name: investmentKey,
                    condition:
                      id <= financialDebtCount &&
                      formData[debtTypeKey] === "Mortgage",
                  })}
                />
                <Field
                  type="checkbox"
                  label=""
                  options={["Close this card"]}
                  {...fieldAdapter({
                    name: closeCardKey,
                    condition:
                      id <= financialDebtCount &&
                      formData[consolidateKey][0] === "Consolidate this card" &&
                      hasUnsecuredRateType,
                  })}
                />
              </FormCard>
            );
          })}
          {(formData.hasDebts === "yes" ||
            isDebtConsolidationPurpose(formData)) &&
            financialDebtCount < maxFinancialDebtCount && (
              <Field
                type="select"
                label={`Add ${financialDebtCount > 0 ? "another " : ""}type`}
                options={debtOptions}
                required={financialDebtCount === 0}
                placeholder="Select an option"
                onChange={addfinancialDebt}
                onBlur={clearSelection}
              ></Field>
            )}
        </>
      </Fieldset>
    </>
  );
};

export function getFieldKeys(id: FinancialDebtId) {
  /**
   * @todo the version of TS we are using does not support literal types
   * If it did, we would be able to use `as const` and it would resolve the types correctly.
   * When we move to a newer version of TS we should make this change
   * */
  const debtTypeKey = `financialDebtType_${id}` as
    | "financialDebtType_1"
    | "financialDebtType_2"
    | "financialDebtType_3"
    | "financialDebtType_4"
    | "financialDebtType_5"
    | "financialDebtType_6"
    | "financialDebtType_7"
    | "financialDebtType_8"
    | "financialDebtType_9"
    | "financialDebtType_10";
  const financeCompanyKey = `financialDebtFinanceCompany_${id}` as
    | "financialDebtFinanceCompany_1"
    | "financialDebtFinanceCompany_2"
    | "financialDebtFinanceCompany_3"
    | "financialDebtFinanceCompany_4"
    | "financialDebtFinanceCompany_5"
    | "financialDebtFinanceCompany_6"
    | "financialDebtFinanceCompany_7"
    | "financialDebtFinanceCompany_8"
    | "financialDebtFinanceCompany_9"
    | "financialDebtFinanceCompany_10";
  const balanceKey = `financialDebtBalance_${id}` as
    | "financialDebtBalance_1"
    | "financialDebtBalance_2"
    | "financialDebtBalance_3"
    | "financialDebtBalance_4"
    | "financialDebtBalance_5"
    | "financialDebtBalance_6"
    | "financialDebtBalance_7"
    | "financialDebtBalance_8"
    | "financialDebtBalance_9"
    | "financialDebtBalance_10";
  const repaymentAmountKey = `financialDebtRepaymentAmount_${id}` as
    | "financialDebtRepaymentAmount_1"
    | "financialDebtRepaymentAmount_2"
    | "financialDebtRepaymentAmount_3"
    | "financialDebtRepaymentAmount_4"
    | "financialDebtRepaymentAmount_5"
    | "financialDebtRepaymentAmount_6"
    | "financialDebtRepaymentAmount_7"
    | "financialDebtRepaymentAmount_8"
    | "financialDebtRepaymentAmount_9"
    | "financialDebtRepaymentAmount_10";
  const repaymentFrequencyKey = `financialDebtRepaymentFrequency_${id}` as
    | "financialDebtRepaymentFrequency_1"
    | "financialDebtRepaymentFrequency_2"
    | "financialDebtRepaymentFrequency_3"
    | "financialDebtRepaymentFrequency_4"
    | "financialDebtRepaymentFrequency_5"
    | "financialDebtRepaymentFrequency_6"
    | "financialDebtRepaymentFrequency_7"
    | "financialDebtRepaymentFrequency_8"
    | "financialDebtRepaymentFrequency_9"
    | "financialDebtRepaymentFrequency_10";
  const isJointLoanKey = `financialDebtIsJointLoan_${id}` as
    | "financialDebtIsJointLoan_1"
    | "financialDebtIsJointLoan_2"
    | "financialDebtIsJointLoan_3"
    | "financialDebtIsJointLoan_4"
    | "financialDebtIsJointLoan_5"
    | "financialDebtIsJointLoan_6"
    | "financialDebtIsJointLoan_7"
    | "financialDebtIsJointLoan_8"
    | "financialDebtIsJointLoan_9"
    | "financialDebtIsJointLoan_10";
  const consolidateKey = `financialDebtConsolidate_${id}` as
    | "financialDebtConsolidate_1"
    | "financialDebtConsolidate_2"
    | "financialDebtConsolidate_3"
    | "financialDebtConsolidate_4"
    | "financialDebtConsolidate_5"
    | "financialDebtConsolidate_6"
    | "financialDebtConsolidate_7"
    | "financialDebtConsolidate_8"
    | "financialDebtConsolidate_9"
    | "financialDebtConsolidate_10";
  const investmentKey = `financialDebtIsInvestment_${id}` as
    | "financialDebtIsInvestment_2"
    | "financialDebtIsInvestment_3"
    | "financialDebtIsInvestment_4"
    | "financialDebtIsInvestment_5"
    | "financialDebtIsInvestment_6"
    | "financialDebtIsInvestment_7"
    | "financialDebtIsInvestment_8"
    | "financialDebtIsInvestment_9"
    | "financialDebtIsInvestment_10";

  const cardLimitKey = `financialDebtCreditCardLimit_${id}` as
    | "financialDebtCreditCardLimit_1"
    | "financialDebtCreditCardLimit_2"
    | "financialDebtCreditCardLimit_3"
    | "financialDebtCreditCardLimit_4"
    | "financialDebtCreditCardLimit_5"
    | "financialDebtCreditCardLimit_6"
    | "financialDebtCreditCardLimit_7"
    | "financialDebtCreditCardLimit_8"
    | "financialDebtCreditCardLimit_9"
    | "financialDebtCreditCardLimit_10";
  const closeCardKey = `financialDebtCloseThisCard_${id}` as
    | "financialDebtCloseThisCard_1"
    | "financialDebtCloseThisCard_2"
    | "financialDebtCloseThisCard_3"
    | "financialDebtCloseThisCard_4"
    | "financialDebtCloseThisCard_5"
    | "financialDebtCloseThisCard_6"
    | "financialDebtCloseThisCard_7"
    | "financialDebtCloseThisCard_8"
    | "financialDebtCloseThisCard_9"
    | "financialDebtCloseThisCard_10";
  return {
    debtTypeKey,
    financeCompanyKey,
    balanceKey,
    repaymentAmountKey,
    repaymentFrequencyKey,
    isJointLoanKey,
    consolidateKey,
    investmentKey,
    cardLimitKey,
    closeCardKey,
  };
}

function isLoanOrMortgage(debtType: string) {
  return debtType === "Personal loan" || debtType === "Mortgage";
}

function isLoanOrCreditCard(debtType: string) {
  return debtType === "Personal loan" || debtType === "Credit card";
}

function showBalance(debtType: string, consolidate: string[]) {
  return (
    isLoanOrMortgage(debtType) ||
    (debtType === "Credit card" && consolidate[0] === "Consolidate this card")
  );
}

const removeDebtItem = (array: Array<Debt>, index: number): Debt[] => {
  return array.slice(0, index).concat(array.slice(index + 1));
};

export const checkForLoanOrCards = (formData: Application): Debt[] => {
  return getDebtFields(formData).filter(
    (debt) => debt.type === "Personal loan" || debt.type === "Credit card"
  );
};

const showDebtConsolidationInfo = (
  formData: Application,
  callouts: Status[] | undefined
): boolean => {
  const loanOrCards = checkForLoanOrCards(formData);
  return (
    isDebtConsolidationPurpose(formData) &&
    loanOrCards.length >= 0 &&
    checkForConsolidationError(callouts)
  );
};

function isDebtConsolidationPurpose(formData: Application): boolean {
  return formData.loanPurpose === "Consolidation";
}

function checkForConsolidationError(callouts: Status[] | undefined): boolean {
  if (!callouts) {
    return true;
  }
  const hasConsolidationError = callouts.some(
    (status) =>
      status.message ===
      "Please ensure that at least one credit card or personal loan is being consolidated"
  );
  return !hasConsolidationError;
}

const checkCallout = (callouts: Status[] | undefined) => {
  return callouts
    ? callouts.some(
        (status) =>
          status.message ===
          "Please ensure that at least one credit card or personal loan is being consolidated"
      )
      ? "Please provide at least one debt for consolidation"
      : false
    : false;
};
